import React from "react";

import { finePrintLabels, splitHospitalFinePrintSections } from "Common/finePrintSections";

import { DetailHeader, DetailContent } from "./DetailRows";

import type { MedicalPlanPrice, Plan } from "Utils/types";

type Props = {
  plans: Array<MedicalPlanPrice>;
};

type CombineOptions = {
  firstKey: string;
  firstLabel: string;
  secondKey: string;
  secondLabel: string;
};

const renderSBCLink = function (plan: Plan) {
  if (plan.has_sbc && plan.sbc_url) {
    return (
      <a target="_blank" rel="nofollow noreferrer" href={plan.sbc_url}>
        Click for full explanation.
      </a>
    );
  }
  const body = `Hello SimplyInsured,\n\nPlease send me a copy of the Summary of Benefits and Coverage for the ${plan.carrier} ${plan.display_name} Plan.\n\nThank you!\n\n\nPlan ID: ${plan.id}\nPlan Name: ${plan.raw_name}`;
  const mailto_url = `mailto:hello+sbc@simplyinsured.com?subject=SBC Request&body=${encodeURIComponent(body)}`;
  return (
    <a href={mailto_url} rel="nofollow">
      Click for full explanation.
    </a>
  );
};

const renderCostCell = function (plan: MedicalPlanPrice, key: string, combineOptions?: CombineOptions) {
  if (typeof plan.estimated_costs[key] === "undefined" && key != "separate_rx_deductible") {
    return null;
  }

  if (combineOptions) {
    const { firstKey, firstLabel, secondKey, secondLabel } = combineOptions;
    const firstInfo = plan.fine_print_updated[firstKey][0];
    const secondInfo = plan.fine_print_updated[secondKey][0];
    // don't show redundant info
    if (firstInfo !== secondInfo) {
      return (
        <>
          <span key={firstLabel}>
            {firstLabel}: {firstInfo}
          </span>
          <br />
          <span key={secondLabel}>
            {secondLabel}: {secondInfo}
          </span>
        </>
      );
    }
  }
  if (plan.fine_print_updated[key][0] === "Click for full explanation.") {
    return <span key={key}>{renderSBCLink(plan)}</span>;
  }
  return <span key={key}>{plan.fine_print_updated[key][0]}</span>;
};

export default function MoreDetails({ plans }: Props) {
  const content = [];

  const toBeCombined = {
    // key: subLabel
    hospitalization_physician: "Doctor's Fees",
    labor: "Delivery",
    surgery_staff: "Surgeon",
  };

  const combineKeys = {
    hospitalization: "hospitalization_physician",
    surgery: "surgery_staff",
    labor_facility: "labor",
  };

  plans.forEach((plan) => {
    const planContent = {};
    finePrintLabels.map(([key, label]) => {
      planContent[label] = [];

      splitHospitalFinePrintSections[key].map((config) => {
        if (toBeCombined.hasOwnProperty(config.key)) {
          return null;
        }
        const [usedLabel, subLabel] = config.label.split(" - ");
        let combineOptions;
        // subLabel can be used to check if we're rendering
        // the overview row, in which case we dont have or
        // want combineOptions
        // Note: now we want to be consistent with how we render hospital terms, so the subLabel logic now applies to the overview row
        if (combineKeys.hasOwnProperty(config.key) && subLabel) {
          const combineKey = combineKeys[config.key];
          combineOptions = {
            firstKey: config.key,
            firstLabel: subLabel,
            secondKey: combineKey,
            secondLabel: toBeCombined[combineKey],
          };
        }

        planContent[label].push(DetailHeader(usedLabel));
        planContent[label].push(DetailContent(renderCostCell(plan, config.key, combineOptions)));
      });
    });
    content.push(planContent);
  });

  return content;
}
